import moment from 'moment';
import { PolicyAdminStatus } from '../../../../common/js/policyAdmin/PolicyAdminStatus';
import { isAccelerated, isAgentEsignRequired } from '../utility/AgentUtilitiesShared';
import { CoverpathUtils } from '../../../../coverpath-common/js/static/CoverpathUtils';
import { POST_ISSUE_STATUS_MAP_Format } from '../types/Types';
import { Assumptions } from '../../../../common/js/static/Assumptions';
import { Exchange1035FundingPurpose } from '../../../../coverpath-common/js/interfaces/CoverpathTypes';

// HL-17855 'financial professional'
const advisorDescriptiveShortNoun = CoverpathUtils.getAdvisorDescriptiveShortNoun().replace(/^\w/, (c) =>
  c.toUpperCase()
);
export const PROGRESS = {
  quoted: 'Quoted',
  applying: 'Applying',
  applied: 'Applied',
  inForce: 'In Force',
};

export const STATUSES = {
  advisorDraft: `Producer Assisted Draft`,
  unopened: 'Link Created',
  opened: 'Link Opened',
  created: 'Created Account',
  ssnVerification: 'SSN Verification',
  idIssue: 'ID Issue',
  driverLicenseIssue: `Driver's License Issue`,
  started: 'Started App',
  submitted: 'App Submitted',
  submittedNoTlic: 'App Submitted (no TLIC)',
  advisorCheck: `Verifying Producer`,
  advisorLicenseIssue: `Producer License Issue`,
  advisorLicenseVerification: `Verifying Producer License`,
  advisorSignature: `Waiting for Producer Signature`,
  eSigned: 'Waiting for Labs',
  labsSched: 'Exam Scheduled',
  labsResched: 'Waiting for Labs',
  labsComplete: 'Processing Labs',
  labsReceived: 'Processing Labs',
  underReview: 'Underwriter Review',
  finalReview: 'Final Review',
  onHold: 'On Hold',
  aps: 'APS Pending',
  followup: 'Waiting for Follow-ups',
  tlicOffer: 'TLIC Offer: [rate_class]',
  tlicOfferAccepted: 'TLIC Offer Accepted',
  expressOffer: 'Express Path Offer: [rate_class]',
  expressOfferAccepted: 'Express Offer Accepted',
  adverse: 'Final offer: adverse decision',
  knockout: 'Final offer: adverse decision',
  finalOffer: 'Final Offer: [rate_class]',
  clientSigned: 'Client Signed App',
  advisorSigned: `Producer Signed App`,
  manualExam: 'Exam',
  free: 'Free Look',
  inForce: 'In Force',
  grace: 'Grace Period',
  lapsed: 'Lapsed',
  lapsedLess: 'Lapsed<60',
  lapsedGreater: 'Lapsed>60',
  declined: 'Declined',
  rejected: 'Rejected',
  ineligible: 'Ineligible',
  canceledByClient: 'Canceled by Client',
  policyLapsedToRPU: 'Reduced Paid-up (RPU)',
  canceledBySystem: 'Canceled by System',
  issuingPolicy: 'Issuing Policy',
  preparingDocs: 'Preparing Documents for Signature',
  conversionInProgress: 'Conversion in Progress',
  awaitingInsuredConsent: 'App Completed',
  awaitingTeleCMICompletion: 'Tele-CMI',
  validateCoverpathPolicies: 'App Submitted',
  conversionCanceled: 'Canceled By Agent',
  terminated: 'Terminated',
};

export const SUBSTATUS = {
  clientMustVerifyBy: 'Client must verify by [MM/DD] at [##:##pm]',
  clientAcceptanceNeeded: 'Client acceptance needed by [MM/DD] at [##:##pm]',
  clientSignatureNeeded: 'Client signature needed by [MM/DD] at [##:##pm]',
  awaitingClientEsign: 'Awaiting Client E-signature',
  awaitingLabs: 'Scheduling Exam, required by [MM/DD]',
  scheduledForWithDateOnly: 'Scheduled for [MM/DD]',
  labNeeded: 'Labs needed by [MM/DD] at [##:##pm]',
  resultsNotRecived: 'Results not yet received',
  awaitingManualExam: 'Awaiting manual exam results',
  inUWQueue: 'In underwriting queue',
  emailSentToClient: 'Email sent to client',
  clientNotEligible: 'Client not eligible',
  runOutTime: 'Ran out of time',
  freeLookEnds: 'Free look ends [MM/DD] at [##:##pm]',
  renewalPremiumDue: 'Renewal premium due [MM/DD]',
  clientMustPay: 'Client must pay by [MM/DD] at [##:##pm]',
  lapsedAfterNoPayment: 'Lapsed after no payments for 90 days',
  notTaken: 'not taken',
  rescinded: 'rescinded',
  replacementReview: 'Replacement under review',
  advisorEsignRequired: `Producer signature needed by [MM/DD] at [##:##pm]`,
  sendToClientBy: 'Send to client by [MM/DD] at [##:##pm]',
  exchange1035NotStartedYetSendToClient:
    '1035 exchange not yet started (send offer preview to client by [MM/DD] at [##:##pm])',
  exchange1035NotStartedYet: '1035 exchange not yet started',
  awaiting1035Funds: 'Awaiting 1035 funds',
  customerSupport: 'Customer support pending',
  termConversionInProgress: 'Conversion to Whole Life in progress',
  termConvertedToWhole: 'Converted to Whole Life',
  wholeConversionInProgress: 'Customer Support Pending',
  wholeConversionInProgressWaitingForClient: 'Waiting for client signatures',
  wholeConversionInProgressWaitingForAgent: 'Waiting for advisor signatures',
  juvenileUnderwriting: 'Juvenile Underwriting',
  InitialPaymentFailed: 'Initial premium payment failed',
  awaitingInsuredConsent: "Awaiting insured's consent",
  awaitingOwnerConsent: "Awaiting owner's consent",
  awaitingInsuredSignature: 'Insured signature needed by [MM/DD] at [##:##pm]',
  awaitingJuvenileInsuredSignature: 'Parent/Guardian signature needed by [MM/DD] at [##:##pm]',
  settingUpDoc: 'Setting up policy documents',
  waiverOfPremium: 'Waiver of Premium is Active',
};
export const NEXTSTATUS = {
  clientClickESign: `The client clicks 'Continue to eSign'.`,
  clientClickESignWithInAppSigning: `The client clicks 'Continue to eSign' and then signs within Coverpath.`,
  encourageToEsign: `The client e-signs via the DocuSign link sent to their email.`,
  encourageToESignWithInAppSigning: `The client eSigns within Coverpath.`,
  idVerification: `The support team emails the client and guides them through the ID verification process.`,
  licenseVerification: `The support team emails the client and guides them through the license verification process.`,
  noActionRequired: `No action required.`,
  clientDecideOnTLIC: `The client declines or accepts (and pays for) TLIC.`,
  clientDecideOnTLICWithInAppSigning: `The client declines or accepts (and pays for) TLIC and then signs within Coverpath.`,
  clientEsign: `Client needs to e-sign through DocuSign sent to the client's email.`,
  advisorFollowUpToScheduleExam: `Advisor/proxy needs to follow up with client to schedule exam with the vendor.`,
  noActionNeeded: `No action needed.`,
  encourageToRescheduleExam: `Encourage your client to reschedule their exam appointment with the vendor.`,
  followUpExam: `Exam vendor has received the order from Coverpath. Follow up with client to make sure exam is scheduled.`,
  vendorContactClient: `The vendor contacts the client by phone or email to schedule the exam.`,
  examOptOut: `You order the medical exam and help your client complete it if they haven’t yet. Exam results are generally received within 5 business days after the exam is completed.`,
  signApplication: `Sign the application.`,
  supportCollectSignatures: `The support team contacts the client to collect additional signatures.`,
  clientEsignNeeded: `The owner e-signs via the DocuSign link sent to their email.`,
  advisorEsignNeeded: `Advisor's e-signature is needed by stated date and time.`,
  clientAcceptanceOfFinalOfferNeeded: `The client clicks 'Accept Offer' and signs their policy documents within Coverpath.`,
  sendOffer: `Edit the offer if needed, then send it to your client. Use Hold Offer if you don’t want the offer to be sent automatically.`,
  sendOfferPreview: `Send a preview of the offer to the client. The client clicks "Approve 1035 exchange funds transfer" to initiate the 1035 exchange.`,
  clientApprove1035: `The client clicks "Approve 1035 exchange funds transfer" to initiate the 1035 exchange.`,
  canSendFinalOffer1035: `After the 1035 funds arrive, you can send the final offer to your client.`,
  shouldSendFinalOffer1035: `Edit the offer, if needed, then send it to your client. Use Hold Offer if you don't want it sent automatically.`,
  reachOutSupport: `Contact support for more details.`,
  contactSupportToCheckRejectionReason: `Contact Support to learn why the client was rejected.`,
  contactSupport: `Please contact Coverpath Support for more details`,
  instructClient: `The client missed multiple premium payments. They can contact the support team to reinstate the policy.`,
  learnMoreRescinding: `The advisor can contact Support to learn more about the rationale behind the rescinding of the policy.`,
  findOutRunOutOfTimeReason: `Contact support for more details. The client can <a href="https://fieldnet.massmutual.com/public/life/pdfs/cancel-and-resubmit-application.pdf" target="_blank">resubmit the application</a> if they are interested in proceeding.`,
  reachOutWithForm:
    'Customer support will reach out to the client with a conversion form, collect client signature, and then they will issue the conversion to Whole Life.',
  awaitingInsuredConsent:
    'The insured gives consent to submit their application by clicking the link in the email they received from MassMutual.',
  awaitingOwnerConsent:
    'The owner gives consent to submit their application by clicking the link in the email they received from MassMutual.',
  awaitingTeleCMICompletion: `The insured calls MassMutual's interview team at ${CoverpathUtils.TELE_CMI_TEAM_PHONE} to complete the Tele-CMI interview.`,
  awaitingPaymentAndClientEsign: `The client completes the conversion by providing their payment information and signing within their account center.`,
  awaitingAgentEsign: `The advisor completes the conversion by signing within their account center.`,
  askingAgentToEsign: `You sign the conversion application. After that, the conversion will be complete.`,
};

export const PA_GENERAL_STATUS_SUBSTATUS_REPLACEMENT = '[SUBSTATUS]';
export const PA_GENERAL_STATUS_SUBSTATUS_WITH_COMMA_LENGTH = PA_GENERAL_STATUS_SUBSTATUS_REPLACEMENT.length + 2;
export const PA_GENERAL_STATUS_MAPPER_SUBSTATUS = {
  DeathClaim: 'Death claim pending',
  WaiverOfPremium: PA_GENERAL_STATUS_SUBSTATUS_REPLACEMENT + ', Waiver of Premium is Active',
};

export const EXAM_NEXTSTATUS = {
  'Applicant on hold': `Follow up with the client to schedule their exam with the vendor.`,
  'Appointment made': NEXTSTATUS.noActionNeeded,
  'Cancelled': `The client reschedules their exam with the vendor.`,
  'CMC-Initial Order Read In': `The vendor has received the exam order. Follow up with the client to make sure the exam gets scheduled.`,
  'Exam Assigned': `The vendor has assigned an examiner. No action is needed.`,
  'Left message for applicant': `The client schedules their exam with the vendor.`,
  'Paramed Scheduled': NEXTSTATUS.noActionNeeded,
  'Exam Scheduled Online': NEXTSTATUS.noActionNeeded,
};
/**
 * for agent account center application statuses prior to application submission.
 */
export const PRE_APP_STATUS_MAP = {
  draftStarted: {
    desc: 'Draft App Started ([progress_percent]% filled)',
    subStatus: 'Not yet sent to client',
    nextStatus: `When you’re ready, send the app to the client by clicking ‘Ready to send’ within the application.`,
  },
  linkCreated: {
    desc: 'Link Created',
    subStatus: 'Client has not yet opened link',
    nextStatus: `The client views their quote by opening the link you emailed to them.`,
  },
  linkOpened: {
    desc: 'Link Opened',
    subStatus: 'Client viewed quote',
    nextStatus: `The client creates their MassMutual account.`,
  },
  acctCreated: {
    desc: 'Account Created',
    subStatus: 'Created account',
    nextStatus: `The client completes the application.`,
  },
  appStarted: {
    desc: 'App Started ([progress_percent]% filled)',
    subStatus: 'Awaiting client submission',
    nextStatus: `The client completes the application.`,
  },
  appCompleted: {
    desc: 'App Started ([progress_percent]% filled)',
    subStatus: 'Awaiting client submission',
    nextStatus: `The client submits the application by navigating to the last section ('Verify') and clicking 'Get My Decision'.`,
  },
};

/**
 * for agent account center application statuses after application submission before going into Policy Admin.
 */
export const POST_APP_STATUS_MAP = {
  'TLIC Application': {
    'Correcting SSN': {
      desc: STATUSES.idIssue,
      subStatus: SUBSTATUS.clientMustVerifyBy,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: -1,
      wfStage: ['FI1'],
      activityName: 'FI1A3 Email Cancel Data Not Fixed',
      nextStatus: NEXTSTATUS.idVerification,
    },
    'Correcting Insured SSN': {
      desc: STATUSES.idIssue,
      subStatus: SUBSTATUS.clientMustVerifyBy,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: -1,
      wfStage: ['FI2'],
      activityName: 'FI2A3 Email Cancel Data Not Fixed',
      nextStatus: NEXTSTATUS.idVerification,
    },
    'Correcting Owner SSN': {
      desc: STATUSES.idIssue,
      subStatus: SUBSTATUS.clientMustVerifyBy,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: -1,
      wfStage: ['FI3'],
      activityName: 'FI3A3 Email Cancel Data Not Fixed',
      nextStatus: NEXTSTATUS.idVerification,
    },
    'Correcting License': {
      desc: STATUSES.driverLicenseIssue,
      subStatus: SUBSTATUS.clientMustVerifyBy,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: -1,
      wfStage: ['FL1'],
      activityName: 'FL1A3 Email Cancel Data Not Fixed',
      nextStatus: NEXTSTATUS.licenseVerification,
    },
    'Waiting for Acceptance': {
      desc: STATUSES.tlicOffer,
      subStatus: SUBSTATUS.clientAcceptanceNeeded,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['TC1'],
      activityName: 'TC1A10 TLIC Offer Auto Decline on Timeout',
      rateClassType: 'tLICRateClass',
      nextStatus: NEXTSTATUS.clientDecideOnTLIC,
    },
    'Waiting for Signature': {
      desc: (policyInfo) => (isAgentEsignRequired(policyInfo) ? STATUSES.clientSigned : STATUSES.tlicOfferAccepted),
      subStatus: (policyInfo) =>
        isAgentEsignRequired(policyInfo) ? SUBSTATUS.advisorEsignRequired : SUBSTATUS.clientSignatureNeeded,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['TC2'],
      activityName: 'TC2A5 Cancel TLIC No Signature',
      nextStatus: (policyInfo) =>
        isAgentEsignRequired(policyInfo) ? NEXTSTATUS.signApplication : NEXTSTATUS.encourageToEsign,
    },
    'Determine Underwriting Class': {
      desc: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return STATUSES.onHold;
        } else {
          return STATUSES.underReview;
        }
      },
      subStatus: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return SUBSTATUS.replacementReview;
        } else {
          return '';
        }
      },
      wfStage: ['UW2'],
      nextStatus: '',
    },
  },
  'Haven Application': {
    'Waiting for Application Acceptance': {
      desc: STATUSES.submittedNoTlic,
      subStatus: SUBSTATUS.clientAcceptanceNeeded,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['MA1'],
      activityName: 'MA1A2 Cancel Application Not Confirmed',
      nextStatus: NEXTSTATUS.clientClickESign,
    },
    'Waiting for Signature': {
      desc: (policyInfo) => (isAgentEsignRequired(policyInfo) ? STATUSES.clientSigned : STATUSES.submittedNoTlic),
      subStatus: (policyInfo) =>
        isAgentEsignRequired(policyInfo) ? SUBSTATUS.advisorEsignRequired : SUBSTATUS.clientSignatureNeeded,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['MA2'],
      activityName: 'MA2A4 Cancel Application Not Confirmed',
      nextStatus: (policyInfo) =>
        isAgentEsignRequired(policyInfo) ? NEXTSTATUS.signApplication : NEXTSTATUS.encourageToEsign,
    },
    'Waiting for Labs': {
      desc: function (policyInfo) {
        if (policyInfo.paramedScheduledDate) {
          return STATUSES.labsSched;
        } else if (policyInfo.examOptOut) {
          return STATUSES.manualExam;
        }
        return policyInfo.replaceInforceApplication ? STATUSES.advisorSigned : STATUSES.clientSigned;
      },
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      subStatus: function (policyInfo) {
        if (policyInfo.isJuvenile) {
          return SUBSTATUS.juvenileUnderwriting;
        } else if (policyInfo.isLiteTouch) {
          return SUBSTATUS.inUWQueue;
        } else if (policyInfo.paramedScheduledDate) {
          return SUBSTATUS.scheduledForWithDateOnly;
        } else if (policyInfo.examOptOut) {
          return SUBSTATUS.awaitingManualExam;
        }
        return SUBSTATUS.awaitingLabs;
      },
      wfStage: ['MA3'],
      nextStatus: function (policyInfo) {
        if (policyInfo.examOptOut) {
          return NEXTSTATUS.examOptOut;
        } else if (policyInfo.isJuvenile || policyInfo.paramedScheduledDate) {
          return ``;
        }
        return NEXTSTATUS.vendorContactClient;
      },
    },
    'Waiting for Advisor Signature': {
      desc: STATUSES.clientSigned,
      subStatus: SUBSTATUS.advisorEsignRequired,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 7,
      wfStage: ['MA4'],
      nextStatus: '',
    },
  },
  'Application Rejected': {
    'With Haven Option': {
      desc: STATUSES.rejected,
      subStatus: SUBSTATUS.clientNotEligible,
      wfStage: ['DN1', 'RE1'],
      nextStatus: NEXTSTATUS.reachOutSupport,
    },
    'With Vantage Option': {
      desc: STATUSES.rejected,
      subStatus: SUBSTATUS.clientNotEligible,
      wfStage: ['DN2', 'RE2'],
      nextStatus: NEXTSTATUS.reachOutSupport,
    },
    'Uninsurable': {
      desc: STATUSES.rejected,
      subStatus: SUBSTATUS.clientNotEligible,
      wfStage: ['DN3', 'RE3'],
      nextStatus: NEXTSTATUS.reachOutSupport,
    },
  },
  'Application Declined': {
    'With Vantage Option': {
      desc: STATUSES.declined,
      subStatus: '',
      wfStage: ['DN4', 'DE1'],
      nextStatus: NEXTSTATUS.reachOutSupport,
    },
    'Uninsurable': {
      desc: STATUSES.declined,
      subStatus: '',
      wfStage: ['DN5', 'DE2'],
      nextStatus: NEXTSTATUS.reachOutSupport,
    },
  },
  'Advisor Amendment': {
    'Waiting for Advisor Amendment': {
      desc: (policyInfo) => (isAccelerated(policyInfo) ? STATUSES.expressOffer : STATUSES.finalOffer),
      subStatus: function (policyInfo) {
        if (policyInfo.alir1035Purpose === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
          return SUBSTATUS.exchange1035NotStartedYetSendToClient;
        }
        return SUBSTATUS.sendToClientBy;
      },
      rateClassType: 'finalRateClass',
      actionRequiredDate: function (actionRequiredDate: Date, offsetDays: number = 0) {
        return moment(actionRequiredDate).add('days', offsetDays).local().toDate();
      },
      importantIfLessThanDays: 3,
      nextStatus: (policyInfo) =>
        policyInfo.alir1035Purpose === Exchange1035FundingPurpose.PREMIUM_AND_ALIR
          ? NEXTSTATUS.sendOfferPreview
          : NEXTSTATUS.sendOffer,
    },
  },
  'Funding Pause': {
    'Initiate Funds Transfer': {
      desc: (policyInfo) => (isAccelerated(policyInfo) ? STATUSES.expressOffer : STATUSES.finalOffer),
      subStatus: SUBSTATUS.exchange1035NotStartedYet,
      rateClassType: 'finalRateClass',
      nextStatus: (policyInfo) =>
        policyInfo.alir1035Purpose === Exchange1035FundingPurpose.PREMIUM_AND_ALIR
          ? NEXTSTATUS.clientApprove1035
          : NEXTSTATUS.sendOffer,
    },
    'Waiting for 1035 Funds': {
      desc: (policyInfo) => (isAccelerated(policyInfo) ? STATUSES.expressOffer : STATUSES.finalOffer),
      subStatus: SUBSTATUS.awaiting1035Funds,
      rateClassType: 'finalRateClass',
      nextStatus: (policyInfo) =>
        policyInfo.alir1035Purpose === Exchange1035FundingPurpose.PREMIUM_AND_ALIR
          ? NEXTSTATUS.canSendFinalOffer1035
          : NEXTSTATUS.sendOffer,
    },
    'Funds have arrived': {
      desc: (policyInfo) => (isAccelerated(policyInfo) ? STATUSES.expressOffer : STATUSES.finalOffer),
      subStatus: SUBSTATUS.sendToClientBy,
      actionRequiredDate: function (actionRequiredDate: Date, offsetMonths: number = 0) {
        return moment(actionRequiredDate).add('months', offsetMonths).local().toDate();
      },
      rateClassType: 'finalRateClass',
      nextStatus: NEXTSTATUS.sendOffer,
    },
  },
  'Funding Pause Canceled': {
    'Ran out of Time': {
      desc: STATUSES.canceledBySystem,
      subStatus: SUBSTATUS.runOutTime,
      wfStage: ['FC1'],
      nextStatus: NEXTSTATUS.findOutRunOutOfTimeReason,
    },
  },
  'Application Canceled By User': {
    'Without TLIC': {
      desc: STATUSES.canceledByClient,
      subStatus: '',
      wfStage: ['CC1', 'CN1'],
      nextStatus: '',
    },
    'With TLIC': {
      desc: STATUSES.canceledByClient,
      subStatus: '',
      wfStage: ['CN2'],
      nextStatus: '',
    },
  },
  'Application Canceled': {
    'Without TLIC': {
      desc: STATUSES.canceledBySystem,
      subStatus: SUBSTATUS.runOutTime,
      wfStage: ['CC2', 'CN3'],
      nextStatus: NEXTSTATUS.findOutRunOutOfTimeReason,
    },
    'Ran out of Time': {
      desc: STATUSES.canceledBySystem,
      subStatus: SUBSTATUS.runOutTime,
      wfStage: ['CC3', 'CN6'],
      nextStatus: NEXTSTATUS.findOutRunOutOfTimeReason,
    },
    'No Labs': {
      desc: STATUSES.canceledBySystem,
      subStatus: SUBSTATUS.runOutTime,
      wfStage: ['CN4'],
      nextStatus: NEXTSTATUS.findOutRunOutOfTimeReason,
    },
    'Not Accepted': {
      desc: STATUSES.canceledBySystem,
      subStatus: SUBSTATUS.runOutTime,
      wfStage: ['CN5'],
      nextStatus: NEXTSTATUS.findOutRunOutOfTimeReason,
    },
  },
  'TLIC Issued': {
    'Final Underwriting': {
      desc: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return STATUSES.onHold;
        } else {
          return STATUSES.underReview;
        }
      },
      subStatus: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return SUBSTATUS.replacementReview;
        } else {
          return '';
        }
      },
      rateClassType: 'finalRateClass',
      wfStage: ['FU1'],
      nextStatus: '',
    },
    'Waiting for Policy Acceptance': {
      desc: STATUSES.finalOffer,
      subStatus: function (policyInfo) {
        if (!policyInfo.isOwnerSameAsInsured && policyInfo.insuredEsignRequired) {
          return policyInfo.isJuvenile
            ? SUBSTATUS.awaitingJuvenileInsuredSignature
            : SUBSTATUS.awaitingInsuredSignature;
        } else {
          return SUBSTATUS.clientAcceptanceNeeded;
        }
      },
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['LI1'],
      activityName: 'LI1A2 Cancel Policy Not Confirmed',
      rateClassType: 'finalRateClass',
      nextStatus: function (policyInfo) {
        if (!policyInfo.isOwnerSameAsInsured && policyInfo.insuredEsignRequired) {
          return ``;
        } else {
          return NEXTSTATUS.clientAcceptanceOfFinalOfferNeeded;
        }
      },
    },
    'Issuing Policy': {
      desc: STATUSES.issuingPolicy,
      subStatus: '',
      wfStage: ['LI2'],
      nextStatus: NEXTSTATUS.noActionNeeded,
    },
    'Waiting for Labs': {
      desc: function (policyInfo) {
        if (policyInfo.paramedScheduledDate) {
          return STATUSES.labsSched;
        } else if (policyInfo.examOptOut) {
          return STATUSES.manualExam;
        }
        return policyInfo.replaceInforceApplication ? STATUSES.advisorSigned : STATUSES.clientSigned;
      },
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      subStatus: function (policyInfo) {
        if (policyInfo.isLiteTouch) {
          return SUBSTATUS.inUWQueue;
        } else if (policyInfo.paramedScheduledDate) {
          return SUBSTATUS.scheduledForWithDateOnly;
        } else if (policyInfo.examOptOut) {
          return SUBSTATUS.awaitingManualExam;
        }
        return SUBSTATUS.awaitingLabs;
      },
      wfStage: ['TC3'],
      nextStatus: function (policyInfo) {
        if (policyInfo.examOptOut) {
          return NEXTSTATUS.examOptOut;
        } else if (policyInfo.paramedScheduledDate || policyInfo.isLiteTouch) {
          return '';
        }
        return NEXTSTATUS.vendorContactClient;
      },
    },
  },
  'Application': {
    'Waiting for Issue Acceptance': {
      desc: STATUSES.finalOffer,
      subStatus: function (policyInfo) {
        if (!policyInfo.isOwnerSameAsInsured && policyInfo.insuredEsignRequired) {
          return policyInfo.isJuvenile
            ? SUBSTATUS.awaitingJuvenileInsuredSignature
            : SUBSTATUS.awaitingInsuredSignature;
        } else {
          return SUBSTATUS.clientAcceptanceNeeded;
        }
      },
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['MI1'],
      activityName: 'MI1A3 Cancel Policy Not Confirmed',
      rateClassType: 'finalRateClass',
      nextStatus: function (policyInfo) {
        if (!policyInfo.isOwnerSameAsInsured && policyInfo.insuredEsignRequired) {
          return ``;
        } else {
          return NEXTSTATUS.clientAcceptanceOfFinalOfferNeeded;
        }
      },
    },
    'Issuing Policy': {
      desc: STATUSES.issuingPolicy,
      subStatus: '',
      nextStatus: NEXTSTATUS.noActionNeeded,
    },
  },
  'Manual Underwriting': {
    'Awaiting APS': {
      desc: STATUSES.aps,
      subStatus: '',
      rateClassType: 'finalRateClass',
      wfStage: ['MU2', 'MU3'],
      nextStatus: '',
    },
    'Final Review': {
      desc: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return STATUSES.onHold;
        } else {
          return STATUSES.underReview;
        }
      },
      subStatus: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return SUBSTATUS.replacementReview;
        } else {
          return '';
        }
      },
      rateClassType: 'finalRateClass',
      wfStage: ['MU1', 'MU4'],
      nextStatus: '',
    },
  },
  'Underwriting': {
    Underwriting: {
      desc: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return STATUSES.onHold;
        } else {
          return STATUSES.underReview;
        }
      },
      subStatus: function (policyInfo) {
        if (policyInfo.isFrozen) {
          return SUBSTATUS.replacementReview;
        } else {
          return '';
        }
      },
      wfStage: ['UW2'],
      nextStatus: '',
    },
  },
  'Accelerated Application': {
    'Waiting for Acceptance': {
      desc: STATUSES.expressOffer,
      subStatus: SUBSTATUS.clientAcceptanceNeeded,
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      wfStage: ['AI1'],
      activityName: 'AI1A3 Cancel Accelerated Not Confirmed',
      rateClassType: 'acceleratedRateClass',
      nextStatus: NEXTSTATUS.clientClickESign,
    },
    'Waiting for Signature': {
      desc: function (policyInfo) {
        return isAgentEsignRequired(policyInfo) ? STATUSES.clientSigned : STATUSES.expressOfferAccepted;
      },
      subStatus: function (policyInfo) {
        return isAgentEsignRequired(policyInfo) ? SUBSTATUS.advisorEsignRequired : SUBSTATUS.clientSignatureNeeded;
      },
      actionRequiredDate: function (actionRequiredDate: Date) {
        return actionRequiredDate;
      },
      importantIfLessThanDays: 3,
      activityName: 'AI2A4 Cancel Accelerated No Signature',
      wfStage: ['AI2'],
      nextStatus: function (policyInfo) {
        return isAgentEsignRequired(policyInfo) ? NEXTSTATUS.signApplication : NEXTSTATUS.clientEsignNeeded;
      },
    },
    'Issuing Policy': {
      desc: STATUSES.issuingPolicy,
      subStatus: '',
      wfStage: ['AI3'],
      nextStatus: NEXTSTATUS.noActionNeeded,
    },
  },
  'License-verification': {
    'Verifying': {
      desc: STATUSES.advisorLicenseVerification,
      subStatus: '',
      importantIfLessThanDays: -1,
      wfStage: ['CS2'],
      nextStatus: '',
    },
    'Verifying Fluidless': {
      desc: STATUSES.advisorLicenseVerification,
      importantIfLessThanDays: -1,
      subStatus: '',
      wfStage: ['CS3'],
      nextStatus: '',
    },
  },
  'Operations Review': {
    'Operations Review': {
      desc: function (policyInfo) {
        if (policyInfo.convertedFrom) {
          return STATUSES.conversionInProgress;
        } else if (policyInfo.applicationSignedDate) {
          return STATUSES.preparingDocs;
        } else {
          return STATUSES.submitted;
        }
      },
      subStatus: function (policyInfo) {
        if (policyInfo.convertedFrom) {
          return SUBSTATUS.wholeConversionInProgress;
        } else if (policyInfo.applicationSignedDate) {
          return SUBSTATUS.customerSupport;
        } else {
          return '';
        }
      },
      wfStage: ['OR1'],
      rateClassType: 'finalRateClass',
      nextStatus: function (policyInfo) {
        if (policyInfo.convertedFrom) {
          return NEXTSTATUS.noActionNeeded;
        } else if (policyInfo.applicationSignedDate) {
          return NEXTSTATUS.supportCollectSignatures;
        } else {
          return '';
        }
      },
    },
    'Issuing Policy': {
      desc: STATUSES.issuingPolicy,
      subStatus: '',
      wfStage: ['CV1'],
      nextStatus: NEXTSTATUS.noActionNeeded,
    },
  },
  'Advisor Decline Review': {
    'Waiting for Advisor': {
      desc: STATUSES.declined,
      subStatus: '',
      importantIfLessThanDays: Assumptions.holdAdvisorDeclineReviewDays,
      wfStage: ['AD1'],
      nextStatus: NEXTSTATUS.reachOutSupport,
    },
  },
  'Consent To Underwriting': {
    'Check Underwriting Consent Requirements': {
      desc: STATUSES.awaitingInsuredConsent,
      subStatus: function (policyInfo) {
        return policyInfo.insuredEsignRequired ? SUBSTATUS.awaitingInsuredConsent : SUBSTATUS.awaitingOwnerConsent;
      },
      wfStage: ['IC1'],
      nextStatus: function (policyInfo) {
        return policyInfo.insuredEsignRequired ? NEXTSTATUS.awaitingInsuredConsent : NEXTSTATUS.awaitingOwnerConsent;
      },
    },
    'Consent To Underwriting': {
      desc: STATUSES.awaitingInsuredConsent,
      subStatus: function (policyInfo) {
        return policyInfo.insuredEsignRequired ? SUBSTATUS.awaitingInsuredConsent : SUBSTATUS.awaitingOwnerConsent;
      },
      wfStage: ['IC1'],
      nextStatus: function (policyInfo) {
        return policyInfo.insuredEsignRequired ? NEXTSTATUS.awaitingInsuredConsent : NEXTSTATUS.awaitingOwnerConsent;
      },
    },
  },
  'Tele CMI': {
    'Tele CMI Needed': {
      desc: STATUSES.awaitingTeleCMICompletion,
      subStatus: '',
      wfStage: ['TE1'],
      nextStatus: NEXTSTATUS.awaitingTeleCMICompletion,
    },
  },
  'Validate Coverpath Policies': {
    'Verifying application data': {
      desc: STATUSES.validateCoverpathPolicies,
      subStatus: '',
      wfStage: ['VC1'],
    },
  },
  'Conversion started': {
    'Conversion started': {
      desc: STATUSES.conversionInProgress,
      subStatus: SUBSTATUS.wholeConversionInProgressWaitingForClient,
      wfStage: ['AC1'],
      nextStatus: NEXTSTATUS.awaitingPaymentAndClientEsign,
    },
  },
  'Conversion is Ready': {
    'Conversion is Ready': {
      desc: STATUSES.conversionInProgress,
      subStatus: SUBSTATUS.wholeConversionInProgressWaitingForAgent,
      wfStage: ['AC2'],
      nextStatus: NEXTSTATUS.awaitingAgentEsign,
    },
  },
  'Conversion Canceled by Agent': {
    'Conversion Canceled by Agent': {
      desc: STATUSES.conversionCanceled,
      subStatus: '',
      wfStage: ['CC5'],
    },
  },
};

/**
 * for agent account center application statuses after going in force in Policy Admin.
 */
export const POST_ISSUE_STATUS_MAP: POST_ISSUE_STATUS_MAP_Format = {
  'Free_Look': {
    status: 'In Force',
    subStatus: function (policyConvertedTo: string, policyConvertedToIsInForce: boolean) {
      return policyConvertedTo && !policyConvertedToIsInForce
        ? SUBSTATUS.termConversionInProgress
        : SUBSTATUS.freeLookEnds;
    },
    actionRequiredDate: function (paStatus: PolicyAdminStatus) {
      return paStatus.getFreeLookEndDate();
    },
    nextStatus: function (policyConvertedTo: string) {
      return policyConvertedTo ? '' : NEXTSTATUS.noActionNeeded;
    },
  },
  'In_Force': {
    status: 'In Force',
    subStatus: function (policyConvertedTo: string) {
      return policyConvertedTo ? SUBSTATUS.termConversionInProgress : '';
    },
    nextStatus: '',
  },
  'In_Force:Recurring_Payment_Failed': {
    status: 'Coverage Pending',
    subStatus: SUBSTATUS.InitialPaymentFailed,
    important: true,
    nextStatus: 'The support team reaches out to you and the client.',
  },
  'In_Force:Pending_Doc_Ready': {
    status: 'In Force',
    subStatus: SUBSTATUS.settingUpDoc,
    nextStatus: '',
  },
  'Grace Period': {
    status: 'Grace Period',
    subStatus: SUBSTATUS.clientMustPay,
    actionRequiredDate: function (paStatus: PolicyAdminStatus) {
      return paStatus.getGraceEndDate();
    },
    importantIfLessThanDays: -1,
    nextStatus: NEXTSTATUS.reachOutSupport,
  },
  'Terminated:Lapsed': {
    status: 'Lapsed',
    subStatus: 'Client must reinstate policy',
    nextStatus: NEXTSTATUS.instructClient,
  },
  'Terminated:Canceled_By_User': {
    status: STATUSES.canceledByClient,
    subStatus: '',
    nextStatus: '',
  },
  'In_Force:Billing_Lapsed': {
    status: STATUSES.policyLapsedToRPU,
    subStatus: '',
    nextStatus: '',
  },
  'Terminated:Not_Taken': {
    status: STATUSES.canceledBySystem,
    subStatus: 'Not Taken',
    nextStatus: NEXTSTATUS.noActionNeeded,
  },
  'Terminated:Rescinded': {
    status: STATUSES.canceledBySystem,
    subStatus: 'Rescinded',
    nextStatus: NEXTSTATUS.reachOutSupport,
  },
  'Terminated:Lapsed_No_Payment_3Years': {
    status: 'Terminated',
    subStatus: 'Lapsed after no client payment for 3 years',
    nextStatus: '',
  },
  'Terminated:Conversion_completed': {
    status: STATUSES.canceledBySystem,
    subStatus: SUBSTATUS.termConvertedToWhole,
    nextStatus: '',
  },
  'Not Found': {
    status: 'Could not load status',
    subStatus: 'Please try again later or contact support',
    nextStatus: '',
  },
};

export const DATE_FORMAT = 'MM/DD/YYYY';
